import React, { CSSProperties, useState } from "react";
import { Helmet } from "react-helmet";
import ReactSelect, {
  components,
  ControlProps,
  IndicatorProps,
  ValueContainerProps,
} from "react-select";

import {
  Container,
  Dialog,
  Icon,
  IconName,
  Pressable,
  Row,
  TextColor,
} from "../components";
import { Amenity, Pricing } from "../data";
import { useQueryString } from "../hooks";
import { formatCurrency } from "../lib/currency/currency";
import { useI18n } from "../lib/i18n/i18n_provider";
import { prepend } from "../lib/js_utils/array_utils";
import { Button } from "./button";
import { Content } from "./content";
import { Footer } from "./footer";
import { Heading } from "./heading";
import { Hidden } from "./hidden";
import { OfferingHeader } from "./offering_header";
import { OfferingFooterMobile } from "./offering_footer_mobile";
import { Picker } from "./picker";
import { Spacer } from "./spacer";
import { FAQ } from "./template_offering_details_base";
import { Text } from "./text";
import { Visible } from "./visible";
import { LightPartnerOffering } from "../data/partner_offering";
import { OfferingRibbon } from "./offering_ribbon";
import { PartnerConfig } from "./partner_config";

interface EmptyStateProps {
  hasFilter: boolean;
  onPressResetFilter: () => void;
}

export function EmptyState(props: EmptyStateProps) {
  const { onPressResetFilter, hasFilter } = props;
  const { fbt } = useI18n();

  return (
    <Container center>
      <Spacer size={56} />
      <Icon size={80} name="search" color="#C8C8C8" />
      <Spacer size={24} />
      <Heading
        customColor="#C8C8C8"
        align="center"
        size="xl"
        className="heading-empty-state"
      >
        {fbt("Sorry, we couldn’t find anything matching your search.")}
      </Heading>
      <Spacer size={80} />
      {hasFilter && (
        <Button
          onPress={onPressResetFilter}
          appearance="outline"
          title={fbt("Reset Filter")}
        />
      )}
      <Spacer size={80} />
    </Container>
  );
}

interface CapacityProps {
  capacity: number;
  color?: TextColor;
}

export function Capacity(props: CapacityProps) {
  const { capacity } = props;
  const { fbt } = useI18n();

  return (
    <Row alignItems="center">
      <Icon name="capacity" />
      <Spacer size={4} direction="horizontal" />
      <Text color={props.color}>
        {capacity > 1
          ? fbt("Up to " + fbt.param("capacity", capacity) + " people")
          : `${capacity} ${fbt("person")}`}
      </Text>
    </Row>
  );
}

interface DayOfficePricingProps {
  pricing: Pricing;
  currency: string | null;
}

export function DayOfficePricing(props: DayOfficePricingProps) {
  const { pricing, currency } = props;
  const { locale, fbt } = useI18n();

  return (
    <Text>
      <Text weight="bold">
        {formatCurrency(pricing.price, currency, locale.languageTag)}
      </Text>{" "}
      {fbt("/hr")}
    </Text>
  );
}

interface AmenitiesProps {
  amenities: Amenity[];
}

export function Amenities(props: AmenitiesProps) {
  const { amenities } = props;

  return (
    <Row wrap>
      {amenities.map((amenity) => (
        <React.Fragment key={amenity.name}>
          <img
            style={{ width: 24, height: 24 }}
            src={amenity.iconURL}
            alt={amenity.name}
          />
          <Spacer size={8} direction="horizontal" />
        </React.Fragment>
      ))}
    </Row>
  );
}

const ValueContainer = (
  props: ValueContainerProps<any, any> & { icon: IconName }
) => {
  const { icon, ...restProps } = props;
  const { locale } = useI18n();

  return (
    <Row alignItems="center" flex={1}>
      <Spacer size={4} direction="horizontal" />
      <Icon name={icon} />
      <Spacer size={4} direction="horizontal" />
      <div
        className={`text text-align-left-${locale.direction}`}
        style={{ flex: 1 }}
      >
        <components.ValueContainer
          {...restProps}
          getStyles={(name, props) => {
            return {
              ...restProps.getStyles(name, props),
            };
          }}
        />
      </div>
    </Row>
  );
};

const ClearIndicator = (props: IndicatorProps<any, any>) => {
  const {
    children = <Icon name="x" size={14} />,
    getStyles,
    innerProps: { ref, ...restInnerProps },
  } = props;
  return (
    <div
      {...restInnerProps}
      ref={ref}
      style={getStyles("clearIndicator", props) as CSSProperties}
      className="clear-indicator"
    >
      <div style={{ paddingTop: "3px", cursor: "pointer" }}>{children}</div>
    </div>
  );
};

const Control = (props: ControlProps<any, any>) => {
  const { ...restProps } = props;

  return (
    <components.Control
      {...restProps}
      getStyles={(name, props) => {
        return {
          ...restProps.getStyles(name, props),
        };
      }}
    />
  );
};

interface CityPickerProps {
  cities: { label: string; value: string }[];
  value?: string;
  onChange: (val?: string) => void;
}

export function CityPicker(props: CityPickerProps) {
  const { cities, value, onChange } = props;
  const { fbt } = useI18n();
  const selected = cities.find((o) => o.value === value) || null;

  const handleChange = React.useCallback(
    (data: any | null) => {
      if (!data) {
        onChange();
      } else {
        onChange(data.value);
      }
    },
    [onChange]
  );

  return (
    <ReactSelect
      styles={pickerStylesConfig}
      value={selected}
      onChange={handleChange}
      placeholder={fbt("City: Any")}
      isClearable
      options={cities}
      components={{
        IndicatorSeparator: null,
        Control: (p) => <Control {...p} />,
        ClearIndicator: (p) => <ClearIndicator {...p} />,
        ValueContainer: (p) => <ValueContainer icon="city" {...p} />,
        DropdownIndicator: () => <Icon name="chevron-down" />,
      }}
    />
  );
}

export function CityPickerMobile(props: CityPickerProps) {
  const { cities, value, onChange } = props;
  const { fbt } = useI18n();
  const handleChange = React.useCallback(
    (data: string) => {
      if (!data) {
        onChange();
      } else {
        onChange(data);
      }
    },
    [onChange]
  );

  return (
    <Picker
      icon="city"
      value={value || ""}
      options={prepend(cities, { label: fbt("City: Any"), value: "" })}
      onChange={handleChange}
      override={pickerOverride}
    />
  );
}

interface StatePickerProps {
  states: { label: string; value: string }[];
  value?: string;
  onChange: (val?: string) => void;
}

export function StatePicker(props: StatePickerProps) {
  const { states, value, onChange } = props;
  const { fbt } = useI18n();

  const selected = states.find((o) => o.value === value) || null;

  const handleChange = React.useCallback(
    (data: any | null) => {
      if (!data) {
        onChange();
      } else {
        onChange(data.value);
      }
    },
    [onChange]
  );

  return (
    <ReactSelect
      styles={pickerStylesConfig}
      value={selected}
      onChange={handleChange}
      placeholder={fbt("State: Any")}
      isClearable
      options={states}
      components={{
        IndicatorSeparator: null,
        Control: (p) => <Control {...p} />,
        ClearIndicator: (p) => <ClearIndicator {...p} />,
        ValueContainer: (p) => <ValueContainer icon="state" {...p} />,
        DropdownIndicator: () => <Icon name="chevron-down" />,
      }}
    />
  );
}

export function StatePickerMobile(props: StatePickerProps) {
  const { states, value, onChange } = props;
  const { fbt } = useI18n();
  const handleChange = React.useCallback(
    (data: string) => {
      if (!data) {
        onChange();
      } else {
        onChange(data);
      }
    },
    [onChange]
  );

  return (
    <Picker
      icon="state"
      value={value || ""}
      options={prepend(states, { label: fbt("State: Any"), value: "" })}
      onChange={handleChange}
      override={pickerOverride}
    />
  );
}

interface ListingLocationPickerProps {
  locations: { label: string; value: string }[];
  value?: string;
  onChange: (val?: string) => void;
}

export function ListingLocationPicker(props: ListingLocationPickerProps) {
  const { locations, value, onChange } = props;
  const { fbt } = useI18n();
  const selected = locations.find((o) => o.value === value) || null;
  const handleChange = React.useCallback(
    (data: any | null) => {
      if (!data) {
        onChange();
      } else {
        onChange(data.value);
      }
    },
    [onChange]
  );

  return (
    <ReactSelect
      styles={pickerStylesConfig}
      value={selected}
      onChange={handleChange}
      isClearable
      placeholder={fbt("Location: Any")}
      options={locations}
      components={{
        IndicatorSeparator: null,
        Control: (p) => <Control {...p} />,
        ClearIndicator: (p) => <ClearIndicator {...p} />,
        ValueContainer: (p) => <ValueContainer icon="location" {...p} />,
        DropdownIndicator: () => <Icon name="chevron-down" />,
      }}
    />
  );
}

export function ListingLocationPickerMobile(props: ListingLocationPickerProps) {
  const { locations, value, onChange } = props;
  const { fbt } = useI18n();
  const handleChange = React.useCallback(
    (data: string) => {
      if (!data) {
        onChange();
      } else {
        onChange(data);
      }
    },
    [onChange]
  );

  return (
    <Picker
      icon="location"
      value={value || ""}
      options={prepend(locations, { label: fbt("Location: Any"), value: "" })}
      onChange={handleChange}
      override={pickerOverride}
    />
  );
}

export interface CapacityOption {
  label: string;
  value: { minCapacity?: number; maxCapacity?: number };
}

export interface FeaturedCapacity {
  minCapacity: number;
  maxCapacity: number;
}

export const DEFAULT_CAPACITY_OPTIONS: CapacityOption[] = [
  { label: "1 - 4", value: { minCapacity: 1, maxCapacity: 4 } },
  { label: "5 - 9", value: { minCapacity: 5, maxCapacity: 9 } },
  { label: "10 - 14", value: { minCapacity: 10, maxCapacity: 14 } },
  { label: "15+", value: { minCapacity: 15 } },
];

interface CapacityPickerProps {
  capacityOptions?: CapacityOption[];
  value?: { minCapacity?: number; maxCapacity?: number };
  onChange: (val?: { minCapacity?: number; maxCapacity?: number }) => void;
}

export function CapacityPicker(props: CapacityPickerProps) {
  const { value, onChange, capacityOptions = DEFAULT_CAPACITY_OPTIONS } = props;
  const { fbt } = useI18n();
  const selected =
    capacityOptions.find((o) => o.value.minCapacity === value?.minCapacity) ||
    null;

  const handleChange = React.useCallback(
    (data: any | null) => {
      if (!data) {
        onChange();
      } else {
        onChange(data.value);
      }
    },
    [onChange]
  );

  return (
    <ReactSelect
      styles={pickerStylesConfig}
      value={selected}
      onChange={handleChange}
      isClearable
      placeholder={fbt("Capacity: Any")}
      options={capacityOptions}
      components={{
        IndicatorSeparator: null,
        Control: (p) => <Control {...p} />,
        ClearIndicator: (p) => <ClearIndicator {...p} />,
        ValueContainer: (p) => <ValueContainer icon="capacity" {...p} />,
        DropdownIndicator: () => <Icon name="chevron-down" />,
      }}
    />
  );
}

export function CapacityPickerMobile(props: CapacityPickerProps) {
  const { value, onChange, capacityOptions = DEFAULT_CAPACITY_OPTIONS } = props;
  const { fbt } = useI18n();
  const handleChange = React.useCallback(
    (data?: number) => {
      const nextValue = capacityOptions.find(
        (c) => c.value.minCapacity === data
      );

      onChange(nextValue?.value);
    },
    [onChange, capacityOptions]
  );

  const options = prepend(
    capacityOptions.map((c) => ({
      label: c.label,
      value: c.value.minCapacity,
    })),
    { label: fbt("Capacity: Any"), value: 0 }
  );

  return (
    <Picker
      icon="capacity"
      value={value?.minCapacity || 0}
      options={options}
      onChange={handleChange}
      override={pickerOverride}
    />
  );
}

const pickerStylesConfig = {
  placeholder: () => ({
    color: "#000",
  }),
};

const pickerOverride = {
  root: {
    style: {
      border: "1px solid #D7D5DD",
      borderRadius: 5,
      paddingTop: 4,
      paddingBottom: 4,
      paddingLeft: 8,
    },
  },
};

interface FAQDialogProps {
  faqs: FAQ[];
}

export function FAQDialog(props: FAQDialogProps) {
  const { faqs } = props;
  const qs = useQueryString();
  const [visible, setVisible] = useState(!!qs.faq);
  const { fbt } = useI18n();

  return (
    <Dialog onRequestClose={() => setVisible(false)} isOpen={visible}>
      <Container maxWidth={600} padding={16}>
        <Pressable onPress={() => setVisible(false)}>
          <Icon name="x" />
        </Pressable>
        <Spacer size={16} />
        <Heading>{fbt("Frequently asked questions")}</Heading>
        <Spacer size={16} />
        {faqs.map((faq) => (
          <Container key={faq.question}>
            <Text weight="bold">{faq.question}</Text>
            <Text>{faq.answer}</Text>
            <Spacer size={16} />
          </Container>
        ))}
        <Spacer size={16} />
        <Spacer />
        <Button onPress={() => setVisible(false)} title={fbt("Close")} />
      </Container>
    </Dialog>
  );
}

interface TemplateOfferingListingLayoutProps {
  showLanguageSelector: boolean;
  showTalkWithUsPrompt?: boolean;
  title: string;
  description: React.ReactNode;
  favicon: string;
  homepageURL: string;
  hubspotForm?: React.ReactNode;
  offeringID: string;
  listingContent: React.ReactNode;
  availableSpaces?: React.ReactNode;
  mobileFilters: React.ReactNode;
  desktopFilters: React.ReactNode;
  faqs?: FAQ[];
  partnerOffering: LightPartnerOffering;
  partnerConfig: PartnerConfig;
  onLocaleSwitch?: () => void;
}

export function TemplateOfferingListingLayout(
  props: TemplateOfferingListingLayoutProps
) {
  const {
    title,
    description,
    favicon,
    showLanguageSelector,
    showTalkWithUsPrompt,
    hubspotForm,
    offeringID,
    homepageURL,
    listingContent,
    availableSpaces,
    mobileFilters,
    desktopFilters,
    partnerOffering,
    partnerConfig,
    faqs,
    onLocaleSwitch,
  } = props;

  return (
    <Container>
      <Helmet>
        <title>{title}</title>
        <link href={favicon} rel="shortcut icon" type="image/x-icon" />
      </Helmet>
      <OfferingHeader
        showLanguageSelector={showLanguageSelector}
        localeOptions={partnerConfig.localeOptions}
        showTalkWithUsPrompt={showTalkWithUsPrompt}
        hubspotForm={hubspotForm}
        offeringID={offeringID}
        logo={partnerOffering.partner.logoUrl}
        partnerID={partnerOffering.partner.id}
        homepageURL={partnerOffering.partner.homepageUrl || homepageURL}
        onLocaleSwitch={onLocaleSwitch}
      />
      <Spacer size={16} />
      <Content>
        <Container paddingHorizontal={16}>
          <Heading size="xl">{title}</Heading>
          <Spacer size={8} />
          <Text>
            {partnerOffering.description ? (
              <div
                dangerouslySetInnerHTML={{
                  __html: partnerOffering.description,
                }}
              />
            ) : (
              description
            )}
          </Text>
          <Spacer size={24} />
          <Visible md>{desktopFilters}</Visible>
          <Spacer size={24} />
          {listingContent}
          {availableSpaces}
        </Container>
      </Content>
      <OfferingFooterMobile
        showLanguageSelector={showLanguageSelector}
        hubspotForm={hubspotForm}
        partnerID={partnerOffering.partner.id}
        offeringID={offeringID}
        showTalkWithUsPrompt={showTalkWithUsPrompt}
        localeOptions={partnerConfig.localeOptions}
        onLocaleSwitch={onLocaleSwitch}
      />
      <Spacer size={80} />
      <Visible md>
        <Footer displayAccessibility={partnerConfig.displayAccessibility} />
      </Visible>
      <Hidden md>
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0 }}>
          {mobileFilters}
        </div>
      </Hidden>
      {faqs && <FAQDialog faqs={faqs} />}
    </Container>
  );
}

interface ItemCardHeaderProps {
  coverImage: {
    src: string;
    alt: string;
  };
  title: string;
  largeTitle?: boolean;
  subTitle: string;
  featuredPromoDescription?: string;
}

export function ItemCardHeader(props: ItemCardHeaderProps) {
  const { coverImage, title, largeTitle, subTitle, featuredPromoDescription } =
    props;

  return (
    <Container flex={1}>
      <Container overflow="hidden">
        {featuredPromoDescription && (
          <OfferingRibbon ribbonMessage={featuredPromoDescription} />
        )}
        <img
          alt={coverImage.alt}
          style={{
            objectFit: "cover",
            width: "100%",
            height: 204,
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
          }}
          src={coverImage.src}
        />
      </Container>
      <Spacer size={8} />
      <Container padding={8} flex={1}>
        {largeTitle ? (
          <Heading>{title}</Heading>
        ) : (
          <Text overflow="ellipsis" weight="bold">
            {title}
          </Text>
        )}
        <Spacer size={4} />
        <Text overflow="ellipsis">{subTitle}</Text>
      </Container>
    </Container>
  );
}
